import React, { useMemo } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Stack } from 'expo-router';
import { Palette, radius, spacing, useThemePalette } from './theme';

export const metadata = {
  title: 'Privacy Policy - RemoteWork',
  description:
    'Privacy Policy for the RemoteWork app — what information is collected, how it is used, and how we protect it.',
  robots: 'index, follow',
};

const CONTACT_EMAIL = 'remoteworkapp@protonmail.com';
const LAST_UPDATED = 'June 24, 2026';

export default function PrivacyPolicy() {
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);

  return (
    <SafeAreaView style={styles.safeArea} edges={['top', 'bottom']}>
      <Stack.Screen options={{ title: 'Privacy Policy' }} />
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.card}>
          <Text style={styles.title}>Privacy Policy</Text>
          <Text style={styles.paragraph}>
            This Privacy Policy explains how information is collected and used by the RemoteWork
            application (the &quot;App&quot;).
          </Text>
          <Text style={styles.paragraph}>Last updated: {LAST_UPDATED}</Text>

          <Text style={styles.sectionTitle}>Using the App Without an Account</Text>
          <Text style={styles.paragraph}>
            You can browse jobs, save favorites, build resumes, and track applications without
            creating an account. In that case your data stays only on your device and is not sent
            to our servers (third-party services below may still collect technical data).
          </Text>

          <Text style={styles.sectionTitle}>Information We Collect</Text>
          <View style={styles.bullet}>
            <Text style={styles.bulletDot}>•</Text>
            <Text style={styles.bulletText}>
              <Text style={styles.bold}>Account information:</Text> if you create an account
              (email &amp; password, Google, Apple, or anonymous sign-in), we collect a unique
              account identifier and, for email/Google/Apple, your email address. Authentication
              and account storage are handled by our service providers.
            </Text>
          </View>
          <View style={styles.bullet}>
            <Text style={styles.bulletDot}>•</Text>
            <Text style={styles.bulletText}>
              <Text style={styles.bold}>Content you create:</Text> when you are signed in, the
              content you add — resumes (which may include your name, email, phone number,
              location, links, and profile photo), tracked applications, favorite jobs, and saved
              job alerts — is backed up to your account so it syncs across your devices.
            </Text>
          </View>
          <View style={styles.bullet}>
            <Text style={styles.bulletDot}>•</Text>
            <Text style={styles.bulletText}>
              <Text style={styles.bold}>Push notification token:</Text> if you create a job alert,
              we store a device messaging token so we can notify you when new jobs match your
              saved alerts.
            </Text>
          </View>
          <View style={styles.bullet}>
            <Text style={styles.bulletDot}>•</Text>
            <Text style={styles.bulletText}>
              <Text style={styles.bold}>Analytics &amp; crash reporting:</Text> we use
              third-party analytics and crash-reporting tools to collect usage and crash data such
              as device information, app interactions, and IP address, to keep the App stable and
              improve it.
            </Text>
          </View>
          <View style={styles.bullet}>
            <Text style={styles.bulletDot}>•</Text>
            <Text style={styles.bulletText}>
              <Text style={styles.bold}>AdMob:</Text> used to display advertisements in the App.
              AdMob may collect device information and location data to provide personalized ads.
            </Text>
          </View>

          <Text style={styles.sectionTitle}>How We Use Information</Text>
          <View style={styles.bullet}>
            <Text style={styles.bulletDot}>•</Text>
            <Text style={styles.bulletText}>
              To provide the App and sync your content across your devices when you are signed in.
            </Text>
          </View>
          <View style={styles.bullet}>
            <Text style={styles.bulletDot}>•</Text>
            <Text style={styles.bulletText}>
              To send the job-alert notifications you have asked for.
            </Text>
          </View>
          <View style={styles.bullet}>
            <Text style={styles.bulletDot}>•</Text>
            <Text style={styles.bulletText}>To improve the App and display advertisements.</Text>
          </View>

          <Text style={styles.sectionTitle}>Where Your Data Is Stored</Text>
          <Text style={styles.paragraph}>
            Account and synced data are stored securely on cloud infrastructure operated by our
            service providers. We do not sell your personal information.
          </Text>

          <Text style={styles.sectionTitle}>Data Retention</Text>
          <Text style={styles.paragraph}>
            We keep your account and synced data for as long as your account exists. When you delete
            your account, the data synced to it is removed. Data stored only on your device is
            removed when you delete it in the App or uninstall the App. Analytics and advertising
            data are retained according to our providers&apos; policies.
          </Text>

          <Text style={styles.sectionTitle}>Deleting Your Data</Text>
          <Text style={styles.paragraph}>
            You can permanently delete your account and all synced data at any time from{' '}
            <Text style={styles.bold}>Settings → Account → Delete account</Text> inside the App.
            This removes your resumes, applications, favorites, and alerts from our servers and
            cannot be undone. Signing out keeps your data; only deletion removes it. You may also
            request deletion by emailing us at <Text style={styles.link}>{CONTACT_EMAIL}</Text>.
          </Text>

          <Text style={styles.sectionTitle}>Children</Text>
          <Text style={styles.paragraph}>
            The App is not directed to children under 13, and we do not knowingly collect personal
            information from them.
          </Text>

          <Text style={styles.sectionTitle}>Security</Text>
          <Text style={styles.paragraph}>
            The App takes reasonable measures to protect user information. However, no method of
            transmission or storage is completely secure, and the App cannot guarantee absolute
            security.
          </Text>

          <Text style={styles.sectionTitle}>Changes to this Privacy Policy</Text>
          <Text style={styles.paragraph}>
            The App may update this Privacy Policy from time to time. The updated Privacy Policy
            will be posted on this page, and the date of the update will be indicated at the top of
            the page.
          </Text>

          <Text style={styles.sectionTitle}>Contact Us</Text>
          <Text style={styles.paragraph}>
            If you have any questions about this Privacy Policy, please contact us at{' '}
            <Text style={styles.link}>{CONTACT_EMAIL}</Text>.
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const PRIVACY_MAX_WIDTH = 760;

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    safeArea: { flex: 1, backgroundColor: p.canvas },
    content: {
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.xl,
      width: '100%',
      maxWidth: PRIVACY_MAX_WIDTH,
      alignSelf: 'center',
    },
    card: {
      backgroundColor: p.surface,
      borderRadius: radius.lg,
      padding: spacing.xl,
      borderWidth: 1,
      borderColor: p.border,
    },
    title: {
      fontSize: 28,
      fontWeight: '700',
      color: p.textPrimary,
      marginBottom: spacing.lg,
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: p.textPrimary,
      marginTop: spacing.lg,
      marginBottom: spacing.sm,
    },
    paragraph: {
      fontSize: 15,
      lineHeight: 22,
      color: p.textSecondary,
      marginBottom: spacing.sm,
    },
    bullet: {
      flexDirection: 'row',
      marginBottom: spacing.sm,
      paddingLeft: spacing.sm,
    },
    bulletDot: {
      fontSize: 15,
      lineHeight: 22,
      color: p.textSecondary,
      marginRight: spacing.sm,
    },
    bulletText: {
      flex: 1,
      fontSize: 15,
      lineHeight: 22,
      color: p.textSecondary,
    },
    bold: { fontWeight: '700', color: p.textPrimary },
    link: { color: p.secondary, fontWeight: '600' },
  });
